import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ShieldCheck, Camera, Users, MessageSquare, Play, Film, ArrowRight, Share2, TrendingUp } from "lucide-react";
import YSVLogo from "@/components/YSVLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PermissionPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    org_position: "",
    phone: "",
    email: "",
    org_name: "",
    team_count: "",
    home_location: "",
    primary_day: "",
  });

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.first_name.trim() || !form.last_name.trim()) { setError("Please enter your first and last name."); return; }
    if (!form.org_position.trim()) { setError("Please tell us your role at the organization."); return; }
    if (!form.email.trim() || !form.email.includes("@")) { setError("Please enter a valid email."); return; }
    if (!form.phone.trim()) { setError("Please enter a phone number."); return; }
    if (!form.org_name.trim()) { setError("Please enter your organization name."); return; }
    if (!form.team_count.trim()) { setError("Please tell us how many teams you have."); return; }
    if (!form.home_location.trim()) { setError("Please enter your home site / location."); return; }
    if (!form.primary_day) { setError("Please pick your primary game day."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/permission-grants`, form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="permission-thankyou" style={{
        minHeight: "100vh",
        background: "radial-gradient(1000px 700px at 50% 0%, rgba(0,229,255,0.10), transparent 60%), var(--dark-bg)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "80px 24px 60px", textAlign: "center",
      }}>
        <div style={{ maxWidth: 900, width: "100%" }}>
          <div style={{ display: "inline-flex", marginBottom: 32 }}><YSVLogo size="hero" /></div>
          <h1 style={{
            fontFamily: "'Outfit', sans-serif", fontWeight: 900,
            fontSize: "clamp(72px, 18vw, 180px)", lineHeight: 0.95, letterSpacing: "-0.03em",
            color: "#fff", textShadow: "0 0 40px rgba(0,229,255,0.30)", margin: "8px 0 20px",
          }}>THANK YOU</h1>
          <p style={{
            fontSize: "clamp(20px, 3vw, 30px)", color: "var(--text-secondary)",
            fontWeight: 500, maxWidth: 640, margin: "0 auto 40px", lineHeight: 1.5,
          }}>
            Permission received. We'll be in touch shortly to schedule your GameDay Operator.
          </p>
          <Link
            to="/landing"
            data-testid="permission-thankyou-home-link"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              color: "var(--cyan)", fontSize: 16, fontWeight: 600, textDecoration: "none",
              padding: "14px 28px", border: "1px solid var(--cyan)", borderRadius: 999,
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--cyan)"; e.currentTarget.style.color = "#0a0a0c"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--cyan)"; }}
          >
            <ArrowLeft size={18} /> VISIT YSV
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="permission-page" style={{ background: "var(--dark-bg)", minHeight: "100vh" }}>
      {/* Hero */}
      <section style={{
        padding: "96px 24px 60px",
        background: "radial-gradient(1100px 600px at 50% 0%, rgba(0,229,255,0.08), transparent 60%)",
        textAlign: "center",
      }}>
        <div style={{ maxWidth: 920, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", marginBottom: 32 }}>
            <YSVLogo size="hero" />
          </div>
          <p style={{
            fontSize: 12, color: "var(--cyan)", fontWeight: 700,
            letterSpacing: 2, textTransform: "uppercase", marginTop: 18,
          }}>YSV Filming Permission</p>
          <h1 style={{
            fontFamily: "'Outfit', sans-serif", fontWeight: 900,
            fontSize: "clamp(34px, 5.5vw, 56px)",
            lineHeight: 1.05, letterSpacing: "-0.02em", color: "#fff",
            marginTop: 10,
          }}>
            Give YSV Permission to Film at Your Site This Fall
          </h1>
          <p style={{
            color: "var(--text-secondary)", fontSize: "clamp(16px, 2vw, 20px)",
            maxWidth: 720, margin: "20px auto 0", lineHeight: 1.65,
          }}>
            Grant YSV permission to send a dedicated GameDay Operator to your site to film
            <strong style={{ color: "#fff" }}> all the games for every age level this fall.</strong>
            <br/><br/>
            <span style={{ color: "var(--cyan)", fontWeight: 600 }}>There is absolutely no cost to allow us to film.</span>
          </p>

          {!showForm && (
            <button
              data-testid="permission-grant-btn"
              onClick={() => setShowForm(true)}
              style={{
                marginTop: 32, padding: "16px 36px",
                background: "var(--cyan)", color: "#0a0a0c",
                border: "none", borderRadius: 999, fontSize: 16, fontWeight: 800,
                cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10,
                fontFamily: "'DM Sans', sans-serif",
                boxShadow: "0 0 40px rgba(0,229,255,0.30)",
                transition: "transform 0.15s ease",
              }}
              onMouseEnter={e => e.currentTarget.style.transform = "scale(1.04)"}
              onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
            >
              <ShieldCheck size={20} /> Grant Permission
            </button>
          )}
        </div>
      </section>

      {/* Form */}
      {showForm && (
        <section style={{ padding: "20px 24px 60px" }}>
          <div className="glass-card" style={{ maxWidth: 720, margin: "0 auto", padding: 32 }}>
            <h2 style={{
              fontFamily: "'Outfit', sans-serif", color: "#fff",
              fontSize: 24, fontWeight: 800, marginBottom: 6,
            }}>Grant YSV Filming Permission</h2>
            <p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 22 }}>
              We'll follow up to coordinate your GameDay Operator and schedule.
            </p>

            <form data-testid="permission-form" onSubmit={submit} style={{ display: "grid", gap: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="First name *" testid="perm-first-name" value={form.first_name} onChange={update("first_name")} required />
                <Field label="Last name *" testid="perm-last-name" value={form.last_name} onChange={update("last_name")} required />
              </div>
              <Field label="Position at the organization *" testid="perm-position" value={form.org_position} onChange={update("org_position")} placeholder="e.g., President, Board Member, Coach Director" required />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Phone *" testid="perm-phone" type="tel" value={form.phone} onChange={update("phone")} required />
                <Field label="Email *" testid="perm-email" type="email" value={form.email} onChange={update("email")} required />
              </div>
              <Field label="Organization name *" testid="perm-org-name" value={form.org_name} onChange={update("org_name")} required />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="How many teams? *" testid="perm-team-count" type="number" min="1" value={form.team_count} onChange={update("team_count")} required />
                <div>
                  <label style={labelStyle}>Primary game day *</label>
                  <select
                    data-testid="perm-primary-day"
                    className="input-field"
                    value={form.primary_day}
                    onChange={update("primary_day")}
                    required
                    style={{ marginTop: 4 }}
                  >
                    <option value="">— Pick one —</option>
                    <option value="Saturday">Saturday</option>
                    <option value="Sunday">Sunday</option>
                    <option value="Both">Both Saturday & Sunday</option>
                  </select>
                </div>
              </div>
              <Field label="Home site / location *" testid="perm-location" value={form.home_location} onChange={update("home_location")} placeholder="e.g., Robbins Park, Robbins IL" required />

              {error && <p data-testid="perm-error" style={{ color: "#f44336", fontSize: 13, margin: 0 }}>{error}</p>}

              <button
                type="submit"
                data-testid="perm-submit"
                disabled={submitting}
                className="btn-primary"
                style={{
                  marginTop: 6, padding: "14px 20px", fontSize: 15, fontWeight: 700,
                  opacity: submitting ? 0.6 : 1, cursor: submitting ? "not-allowed" : "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10,
                }}
              >
                <ShieldCheck size={16} /> {submitting ? "Submitting…" : "Grant Permission"}
              </button>
            </form>
          </div>
        </section>
      )}

      {/* Tools showcase */}
      <section style={{ padding: "40px 24px 80px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", textAlign: "center" }}>
          <p style={{
            fontSize: 12, color: "var(--cyan)", fontWeight: 700,
            letterSpacing: 2, textTransform: "uppercase", marginBottom: 14,
          }}>What you get</p>
          <h2 style={{
            fontFamily: "'Outfit', sans-serif", color: "#fff",
            fontSize: "clamp(22px, 3vw, 32px)", fontWeight: 800, marginBottom: 32,
          }}>
            Tools your coaches, players and parents will love
          </h2>
          <div style={{
            display: "grid", gap: 18,
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            maxWidth: 1200, margin: "0 auto",
          }}>
            <ToolCard kind="games" />
            <ToolCard kind="clips" />
            <ToolCard kind="highlights" />
            <ToolCard kind="roster" />
          </div>
        </div>
      </section>
    </div>
  );
};

const labelStyle = { fontSize: 12, color: "var(--text-secondary)", display: "block" };

const Field = ({ label, testid, value, onChange, type = "text", placeholder, required, min }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    <input
      data-testid={testid}
      className="input-field"
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      min={min}
      style={{ marginTop: 4 }}
    />
  </div>
);

// ── Tool cards: visual mockups (not real screenshots) — small, branded
//    illustrations of each in-app feature so the page works even before we
//    have polished marketing screenshots. ──
const ToolCard = ({ kind }) => {
  const cards = {
    games: {
      icon: Camera,
      title: "Games",
      copy: "Watch every game replayed in HD. Zoom, slow-mo, and frame-by-frame review — any device, anytime.",
      mock: <GamesMock />,
    },
    clips: {
      icon: MessageSquare,
      title: "Clips",
      copy: "Cut moments straight from any game and share them with teammates and coaches instantly.",
      mock: <ClipsMock />,
    },
    highlights: {
      icon: Film,
      title: "Highlight Tapes",
      copy: "Build a highlight tape and share it with scouts, family, and fans across social media.",
      mock: <HighlightsMock />,
    },
    roster: {
      icon: Users,
      title: "Roster & Schedule Management",
      copy: "Manage rosters, invite parents, run age verification, and post the full season schedule from one place.",
      mock: <RosterMock />,
    },
  };
  const c = cards[kind];
  const Icon = c.icon;
  return (
    <div data-testid={`tool-card-${kind}`} className="glass-card" style={{
      padding: 18, display: "flex", flexDirection: "column", gap: 12,
      textAlign: "left", background: "rgba(20,20,28,0.6)",
    }}>
      <div style={{ aspectRatio: "16/10", borderRadius: 10, overflow: "hidden", background: "#0a0a0c", border: "1px solid var(--dark-border)" }}>
        {c.mock}
      </div>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <Icon size={16} style={{ color: "var(--cyan)" }} />
          <h3 style={{ color: "#fff", fontSize: 15, fontWeight: 800, fontFamily: "'Outfit', sans-serif" }}>{c.title}</h3>
        </div>
        <p style={{ color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.55 }}>{c.copy}</p>
      </div>
    </div>
  );
};

// ── Real screenshot of the YSV video player in action ──
const GamesMock = () => (
  <div style={{ position: "relative", width: "100%", height: "100%", background: "#000", overflow: "hidden" }}>
    <img
      src="/games-screenshot.jpeg"
      alt="YSV video player in action"
      style={{
        width: "100%", height: "100%",
        objectFit: "cover",
        // Anchor to the top so the gameplay frame stays visible — the
        // real screenshot has app controls below the video that we let
        // get clipped on small card sizes.
        objectPosition: "center top",
        display: "block",
      }}
    />
  </div>
);

const RosterMock = () => (
  <div style={{ position: "relative", width: "100%", height: "100%", background: "#11141b", padding: 12, display: "flex", gap: 10 }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ color: "#fff", fontSize: 10, fontWeight: 700, marginBottom: 6, fontFamily: "'Outfit', sans-serif" }}>Hellcats 11U — Roster</div>
      {["#7  Jordan B.", "#22 Marcus T.", "#34 Devon R.", "#41 Cam W."].map((n, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 0", borderBottom: i < 3 ? "1px solid #1f242c" : "none" }}>
          <div style={{ width: 14, height: 14, borderRadius: "50%", background: "var(--cyan)", color: "#000", fontSize: 8, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>{n.charAt(n.length - 1)}</div>
          <span style={{ color: "#d7d7e0", fontSize: 9, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n}</span>
        </div>
      ))}
    </div>
    <div style={{ flex: 1, minWidth: 0, borderLeft: "1px solid #1f242c", paddingLeft: 10 }}>
      <div style={{ color: "#fff", fontSize: 10, fontWeight: 700, marginBottom: 6, fontFamily: "'Outfit', sans-serif" }}>Schedule</div>
      {[
        { date: "Sat 10/5", opp: "vs Broncos", loc: "Home" },
        { date: "Sat 10/12", opp: "@ Titans", loc: "Away" },
        { date: "Sat 10/19", opp: "vs Ravens", loc: "Home" },
        { date: "Sat 10/26", opp: "@ Colts", loc: "Away" },
      ].map((g, i) => (
        <div key={i} style={{ padding: "3px 0", borderBottom: i < 3 ? "1px solid #1f242c" : "none" }}>
          <div style={{ color: "var(--cyan)", fontSize: 8, fontWeight: 700 }}>{g.date}</div>
          <div style={{ color: "#d7d7e0", fontSize: 9, display: "flex", justifyContent: "space-between" }}>
            <span>{g.opp}</span>
            <span style={{ color: g.loc === "Home" ? "#4caf50" : "#ff9800", fontWeight: 600 }}>{g.loc}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Highlight Tape builder — shows clip strip + "Share to social" CTA
const HighlightsMock = () => (
  <div style={{ position: "relative", width: "100%", height: "100%", background: "#11141b", padding: 12 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
      <div style={{ color: "#fff", fontSize: 11, fontWeight: 700, fontFamily: "'Outfit', sans-serif", display: "flex", alignItems: "center", gap: 5 }}>
        <Film size={12} style={{ color: "var(--cyan)" }} />
        Highlight Tape
      </div>
      <div style={{ color: "#4caf50", fontSize: 9, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 3 }}>
        <TrendingUp size={9} /> 2.1K views
      </div>
    </div>
    {/* Clip strip */}
    <div style={{ display: "flex", gap: 3, marginBottom: 8 }}>
      {["#7", "#22", "#41", "#7", "#34"].map((num, i) => (
        <div key={i} style={{
          flex: 1, aspectRatio: "16/10",
          background: `linear-gradient(135deg, rgba(0,229,255,${0.18 + i * 0.04}), rgba(0,0,0,0.6))`,
          border: "1px solid #2a2a35", borderRadius: 3,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 9, fontWeight: 800, color: "#fff",
          fontFamily: "'Outfit', sans-serif",
        }}>{num}</div>
      ))}
    </div>
    {/* Share targets */}
    <div style={{ display: "flex", gap: 5, marginBottom: 6, alignItems: "center" }}>
      <span style={{ color: "#888", fontSize: 9 }}>Share to:</span>
      {["Instagram", "TikTok", "X", "YouTube"].map((s, i) => (
        <span key={i} style={{
          padding: "2px 6px", borderRadius: 3,
          background: "rgba(0,229,255,0.10)", border: "1px solid rgba(0,229,255,0.25)",
          color: "#d7d7e0", fontSize: 8, fontWeight: 600,
        }}>{s}</span>
      ))}
    </div>
    <div style={{ marginTop: 6, padding: "5px 10px", background: "var(--cyan)", color: "#000", borderRadius: 4, fontSize: 10, fontWeight: 700, textAlign: "center", display: "inline-flex", alignItems: "center", gap: 4 }}>
      <Share2 size={10} /> Send to Scouts
    </div>
  </div>
);

const ClipsMock = () => (
  <div style={{ position: "relative", width: "100%", height: "100%", background: "#11141b", padding: 12 }}>
    <div style={{ color: "#fff", fontSize: 11, fontWeight: 700, marginBottom: 8, fontFamily: "'Outfit', sans-serif" }}>Send Clip</div>
    <div style={{ background: "#0a0a0c", border: "1px solid #2a2a35", borderRadius: 6, padding: 6, marginBottom: 8 }}>
      <div style={{ color: "var(--cyan)", fontSize: 9, fontWeight: 700, marginBottom: 2 }}>"Big TD Run"</div>
      <div style={{ color: "#aaa", fontSize: 8 }}>14.2s · From Coach Mike</div>
    </div>
    {["#7 Jordan (teammate)", "#22 Marcus (teammate)", "Coach Rivera"].map((n, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 0" }}>
        <input type="checkbox" defaultChecked={i < 2} readOnly style={{ accentColor: "var(--cyan)", width: 11, height: 11 }} />
        <span style={{ color: "#d7d7e0", fontSize: 10 }}>{n}</span>
      </div>
    ))}
    <div style={{ marginTop: 8, padding: "5px 10px", background: "var(--cyan)", color: "#000", borderRadius: 4, fontSize: 10, fontWeight: 700, textAlign: "center", display: "inline-flex", alignItems: "center", gap: 4 }}>
      Send <ArrowRight size={10} />
    </div>
  </div>
);

export default PermissionPage;
